import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { trackEvent, trackException } from "@/lib/firebase";

export default function PaymentStatusPage({ status = "success" }) {
  const [searchParams] = useSearchParams();
  const txnid = searchParams.get("txnid") || localStorage.getItem("finease_last_txnid") || "";
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const [verifiedStatus, setVerifiedStatus] = useState("");

  const API_BASE_URL = process.env.REACT_APP_API_URL || "https://finease-crm.vercel.app";

  useEffect(() => {
    let active = true;

    async function verify() {
      if (!txnid) return;

      setIsVerifying(true);
      setVerifyError("");

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/payments/verify?txnid=${encodeURIComponent(txnid)}`
        );
        const data = await response.json();

        if (!active) return;

        if (!response.ok) {
          setVerifyError(data.error || "Could not verify payment status.");
          trackEvent("payment_verify_failed", {
            txnid,
            reason: data.error || "verify_failed",
          });
          return;
        }

        const next = String(data.status || "").toLowerCase();
        setVerifiedStatus(next);
        trackEvent("payment_verified", {
          txnid,
          status: next,
          payu_status: String(data.payu_status || ""),
        });
        if (next === "paid" || next === "failed") {
          localStorage.removeItem("finease_last_txnid");
        }
      } catch (err) {
        if (!active) return;
        setVerifyError("Could not verify payment status.");
        trackException("payment_verify_request_failed");
      } finally {
        if (active) setIsVerifying(false);
      }
    }

    verify();
    return () => {
      active = false;
    };
  }, [API_BASE_URL, txnid]);

  const isSuccess = useMemo(() => {
    if (verifiedStatus === "paid") return true;
    if (verifiedStatus === "failed") return false;
    return status === "success";
  }, [verifiedStatus, status]);

  return (
    <main className="pt-28 md:pt-36 min-h-screen bg-[#050505]">
      <section className="max-w-3xl mx-auto px-6 md:px-12 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-[#0A0A0A] border border-white/10 rounded-3xl p-8 md:p-10 text-center"
        >
          <div className="w-20 h-20 rounded-full mx-auto mb-5 flex items-center justify-center border border-white/10 bg-white/5">
            {isSuccess ? (
              <CheckCircle2 className="w-10 h-10 text-[#00C896]" />
            ) : (
              <XCircle className="w-10 h-10 text-red-400" />
            )}
          </div>

          <h1 className="text-3xl md:text-4xl font-semibold text-white mb-3">
            {isSuccess ? "Payment Successful" : "Payment Failed"}
          </h1>

          <p className="text-slate-400 mb-2">
            {isSuccess
              ? "Your payment has been received. Our team will connect with you shortly."
              : "We could not complete your payment. You can retry from the service page."}
          </p>

          {txnid && (
            <div className="mb-3">
              {isVerifying ? (
                <p className="inline-flex items-center gap-2 text-sm text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verifying payment with PayU...
                </p>
              ) : verifyError ? (
                <p className="text-sm text-amber-400">{verifyError}</p>
              ) : verifiedStatus ? (
                <p className="text-sm text-slate-400">
                  Verified Status: <span className="text-slate-200 uppercase">{verifiedStatus}</span>
                </p>
              ) : null}
            </div>
          )}

          {txnid && (
            <p className="text-sm text-slate-500 mb-8">
              Transaction ID: <span className="text-slate-300">{txnid}</span>
            </p>
          )}

          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/services"
              className="inline-flex items-center justify-center bg-[#00C896] text-black font-semibold px-6 py-3 rounded-full hover:bg-[#00E6AC] transition-colors"
            >
              Browse Services
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center border border-white/20 text-white font-medium px-6 py-3 rounded-full hover:bg-white/5 transition-colors"
            >
              Contact Support
            </Link>
          </div>
        </motion.div>
      </section>
    </main>
  );
}
